/**
 * Span row (cross-area UC) helpers — design §3.5.5 / api-doc §五.
 *
 * Convention:
 *   - `solutionArea === '_span_'`        → this entry is a span UC
 *   - `notes === "Engagement|Monetize"`  → pipe-separated list of covered
 *                                          real-area names; EMPTY = all areas
 *
 * The renderer needs to know which physical columns the span row should
 * stretch over. With `areaNames` (the ordered list of non-span areas in the
 * matrix), we compute the start/end column indices.
 */

/** The reserved area name used to mark a row as cross-area. */
export const SPAN_AREA = '_span_';

/** User-facing label for the SPAN_AREA sentinel in Asset-domain UI (the Create
 *  modal's UC dropdown + the Assets Structured nav tree). A span UC is not under
 *  any single sales area — it spans multiple — so we show this instead of the raw
 *  '_span_'. NOTE: the UC Catalog tree (SolutionMap domain) labels the same
 *  sentinel "Cross-cutting"; Asset-domain UI uses "Cross-Area" per product. */
export const SPAN_AREA_LABEL = 'Cross-Area';

/** Display name for a sales-area value: relabels the SPAN_AREA sentinel, leaves
 *  everything else untouched. */
export function displayAreaName(area: string | null | undefined): string {
  return area === SPAN_AREA ? SPAN_AREA_LABEL : (area ?? '');
}

/**
 * Read `entry.notes` defensively. The generated `SolutionMapEntry` DOES declare
 * `notes` now (it did not when this helper was written), so this is no longer an
 * escape hatch around a missing SDK field — it survives because `notes` is optional
 * on the wire and every caller here wants "absent" and "not a string" to collapse
 * into the same `undefined`. Takes `object` rather than `SolutionMapEntry` so mock
 * and partially-built rows can be passed without casts.
 */
export function readEntryNotes(entry: object | null | undefined): string | undefined {
  if (entry == null) return undefined;
  const v = (entry as Record<string, unknown>).notes;
  return typeof v === 'string' ? v : undefined;
}

/** The separator a cross-area UC's covered-column list is stored with, inside `notes`. */
export const COVERAGE_SEPARATOR = '|';

/**
 * Why a column name cannot be anything the user types.
 *
 * Two names break the data model rather than merely looking odd, and both fail SILENTLY,
 * which is why they are refused at the input instead of being left to the backend:
 *
 *   - `_span_` is the sentinel marking a cross-area row, not a real column, and what the
 *     backend does with it DIFFERS by path — which is the whole reason to catch it here.
 *     rename-area / deprecate-area reject it, but with the same 404 they use for "source
 *     column has no live rows", so the client cannot tell those apart and would blame the
 *     wrong column. CREATE does not reject it at all: `UpdateSolutionMap` happily writes a
 *     row with `solution_area = '_span_'`, because that is how a legitimate cross-area UC is
 *     made. So "add a column named _span_" would return 200 and add no column.
 *   - a name containing `|` collides with the separator this module stores coverage lists
 *     with. `serializeSpanCoverage(['Retail | Grocery','EV'])` round-trips back as three
 *     names, only one of which resolves, so `computeSpanRange` silently renders the span
 *     over the wrong columns (or, if none resolve, over ALL of them).
 *
 * Returns a ready-to-show message, or `null` when the name is usable.
 */
export function validateColumnName(name: string): string | null {
  if (name === SPAN_AREA) {
    return `「${SPAN_AREA}」是跨列 UC 的保留名称，不能用作列名`;
  }
  if (name.includes(COVERAGE_SEPARATOR)) {
    return `列名不能包含「${COVERAGE_SEPARATOR}」—— 跨列 UC 的覆盖范围用它分隔，含它的列名会让覆盖范围静默错位`;
  }
  return null;
}

/** Parse `entry.notes` into the list of covered area names (empty = "all"). */
export function parseSpanCoverage(notes: string | null | undefined): string[] {
  return (notes ?? '').split(COVERAGE_SEPARATOR).filter(Boolean);
}

/** Serialize covered area names back to the storage encoding. */
export function serializeSpanCoverage(areas: string[]): string {
  return areas.filter(Boolean).join(COVERAGE_SEPARATOR);
}

export interface SpanColumnRange {
  /** Index of the first covered column in `areaNames`. */
  startCol: number;
  /** Index of the last covered column (inclusive). */
  endCol: number;
}

/**
 * Compute the column range a span row should stretch over.
 *
 * Falls back to (0, areaNames.length - 1) when:
 *  - the covered list is empty (= cover all)
 *  - none of the covered names appear in the current `areaNames`
 *  - `areaNames` is empty
 *
 * This mirrors the prototype's renderIndustryMatrix logic at app.html:741-754.
 */
export function computeSpanRange(
  notes: string | null | undefined,
  areaNames: string[],
): SpanColumnRange {
  const total = areaNames.length;
  if (total === 0) return { startCol: 0, endCol: 0 };

  const covered = parseSpanCoverage(notes);
  if (covered.length === 0) return { startCol: 0, endCol: total - 1 };

  const indices = covered.map((a) => areaNames.indexOf(a)).filter((i) => i >= 0);
  if (indices.length === 0) return { startCol: 0, endCol: total - 1 };

  return { startCol: Math.min(...indices), endCol: Math.max(...indices) };
}

/** Inline-style values for `margin-left` and `width` on the span row. */
export function spanRowStyle(
  range: SpanColumnRange,
  totalCols: number,
): { marginLeft: string; width: string } {
  if (totalCols === 0) return { marginLeft: '0%', width: '100%' };
  const widthPct = ((range.endCol - range.startCol + 1) / totalCols) * 100;
  const offsetPct = (range.startCol / totalCols) * 100;
  return { marginLeft: `${offsetPct.toFixed(1)}%`, width: `${widthPct.toFixed(1)}%` };
}
